// src/components/MasterSiteManager.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./styles/MasterSite.css";
import MapViewer from "./MapViewer";
import { AuthContext } from "../context/AuthContext";

export default function MasterSiteManager({ refreshKey }) {
  const { user } = useContext(AuthContext);

  const isAdmin =
    user?.role === "admin" ||
    user?.role === "super-admin";

  const token =
    sessionStorage.getItem("token") || localStorage.getItem("token") || "";
  const authHeader = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : {};

  const [masterSites, setMasterSites] = useState([]);
  const [siteMap, setSiteMap] = useState({});
  const [reportMap, setReportMap] = useState({});
  const [dispenserTypes, setDispenserTypes] = useState([]);

  const [form, setForm] = useState({
    name: "",
    dispenserType: "",
    dailyDataAvailableFrom: "",
    dailyDataAvailableUpto: "now",
  });
  const [editingId, setEditingId] = useState(null);

  const [reportDate, setReportDate] = useState("");
  const [reportDateObj, setReportDateObj] = useState(null);

  // UI
  const [loading, setLoading] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  // ✅ SEARCH FIX: separate draft vs applied query
  const [draftQuery, setDraftQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");

  const [expanded, setExpanded] = useState({});
  const [mapOpen, setMapOpen] = useState({});

  const parseDecimal = (value) => {
    if (typeof value === "object" && value?.$numberDecimal) {
      return parseFloat(value.$numberDecimal);
    }
    return parseFloat(value || 0);
  };

  const toDDMMYYYY = (date) => {
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const safe = (v) => String(v || "").trim();
  const safeLower = (v) => safe(v).toLowerCase();

  const fetchMasterSites = async () => {
    setErrMsg("");
    setLoading(true);
    try {
      const [msRes, siteRes] = await Promise.all([
        axios.get("/api/mastersites", authHeader),
        axios.get("/api/sites", authHeader),
      ]);

      const ms = Array.isArray(msRes.data) ? msRes.data : [];
      const sites = Array.isArray(siteRes.data) ? siteRes.data : [];

      setMasterSites(ms);

      const map = {};
      sites.forEach((site) => {
        if (!map[site.masterSiteId]) map[site.masterSiteId] = [];
        map[site.masterSiteId].push(site);
      });

      Object.keys(map).forEach((k) => {
        map[k].sort((a, b) => safe(a.name).localeCompare(safe(b.name)));
      });

      setSiteMap(map);

      setExpanded((prev) => {
        if (Object.keys(prev).length) return prev;
        const next = {};
        ms.slice(0, 2).forEach((m) => (next[m._id] = true));
        return next;
      });
    } catch (e) {
      setErrMsg(e?.response?.data?.message || e?.message || "Failed to load master sites");
    } finally {
      setLoading(false);
    }
  };

  const fetchReportsForDate = async (dateStr, masters = masterSites) => {
    if (!dateStr || !masters?.length) {
      setReportMap({});
      return;
    }

    setErrMsg("");
    setLoadingReports(true);
    try {
      const results = await Promise.all(
        masters.map(async (ms) => {
          try {
            const res = await axios.get(
              `/api/reports/daily/master/${ms._id}?from=${dateStr}&to=${dateStr}`,
              authHeader
            );
            return [ms._id, Array.isArray(res.data) ? res.data : []];
          } catch {
            return [ms._id, []];
          }
        })
      );

      setReportMap(Object.fromEntries(results));
    } catch (e) {
      setErrMsg(e?.response?.data?.message || e?.message || "Failed to load reports");
    } finally {
      setLoadingReports(false);
    }
  };

  const exportToExcel = (masterSiteId) => {
    const ms = masterSites.find((m) => m._id === masterSiteId);
    const reports = reportMap[masterSiteId] || [];

    const rows = reports.map((r) => ({
      Site: r.site,
      Phone: r.phoneNumber,
      WaterDispensed_L: parseDecimal(r.totalWaterDispensed),
      AmountCollected_Rs: parseDecimal(r.totalAmountCollected),
    }));

    const total = rows.reduce(
      (acc, row) => {
        acc.WaterDispensed_L += row.WaterDispensed_L;
        acc.AmountCollected_Rs += row.AmountCollected_Rs;
        return acc;
      },
      { WaterDispensed_L: 0, AmountCollected_Rs: 0 }
    );

    rows.push({ Site: "TOTAL", Phone: "", ...total });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, ms?.name || "Report");
    XLSX.writeFile(wb, `${ms?.name || "MasterSite"}_Report_${reportDate || "date"}.xlsx`);
  };

  useEffect(() => {
    fetchMasterSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    const fetchDispenserTypes = async () => {
      try {
        const res = await axios.get("/api/dispenserTypes", authHeader);
        setDispenserTypes(Array.isArray(res.data) ? res.data : []);
      } catch {
        // ignore
      }
    };
    fetchDispenserTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportDate) fetchReportsForDate(reportDate);
    else setReportMap({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDate, masterSites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    try {
      if (editingId) {
        await axios.put(`/api/mastersites/${editingId}`, form, authHeader);
      } else {
        await axios.post("/api/mastersites", { ...form, childSites: [] }, authHeader);
      }

      setForm({
        name: "",
        dailyDataAvailableFrom: "",
        dailyDataAvailableUpto: "now",
        dispenserType: "",
      });
      setEditingId(null);

      await fetchMasterSites();
      if (reportDate) await fetchReportsForDate(reportDate);
    } catch (e2) {
      setErrMsg(e2?.response?.data?.message || e2?.message || "Save failed");
    }
  };

  const handleEdit = (ms) => {
    setEditingId(ms._id);
    setForm({
      name: ms.name,
      dailyDataAvailableFrom: ms.dailyDataAvailableFrom || "",
      dailyDataAvailableUpto: ms.dailyDataAvailableUpto || "now",
      dispenserType: ms.dispenserType || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this master site?")) {
      setErrMsg("");
      try {
        await axios.delete(`/api/mastersites/${id}`, authHeader);
        await fetchMasterSites();
        if (reportDate) await fetchReportsForDate(reportDate);
      } catch (e) {
        setErrMsg(e?.response?.data?.message || e?.message || "Delete failed");
      }
    }
  };

  // ✅ APPLY SEARCH ON CLICK / ENTER
  const runSearch = () => {
    const q = safe(draftQuery);
    setAppliedQuery(q);
    // open results so user can SEE the matches
    if (q) {
      setExpanded((prev) => {
        const next = { ...prev };
        masterSites.forEach((ms) => {
          next[ms._id] = true;
        });
        return next;
      });
    }
  };

  const clearSearch = () => {
    setDraftQuery("");
    setAppliedQuery("");
  };

  // ✅ FILTER USES appliedQuery (NOT draftQuery)
  const filteredMasterSites = useMemo(() => {
    const q = safeLower(appliedQuery);
    if (!q) return masterSites;

    return masterSites.filter((ms) => {
      const msName = safeLower(ms.name);
      const msType = safeLower(ms.dispenserType);
      const sites = siteMap[ms._id] || [];

      const siteHit = sites.some((s) => {
        const nm = safeLower(s.name);
        const ph = safeLower(s.phoneNumber);
        return nm.includes(q) || ph.includes(q);
      });

      return msName.includes(q) || msType.includes(q) || siteHit;
    });
  }, [masterSites, appliedQuery, siteMap]);

  const totals = useMemo(() => {
    let water = 0;
    let amount = 0;
    masterSites.forEach((ms) => {
      const reports = reportMap[ms._id] || [];
      water += reports.reduce((sum, r) => sum + parseDecimal(r.totalWaterDispensed), 0);
      amount += reports.reduce((sum, r) => sum + parseDecimal(r.totalAmountCollected), 0);
    });
    return { water, amount };
  }, [masterSites, reportMap]);

  const stats = useMemo(() => {
    const totalMasters = masterSites.length;
    const totalSites = Object.values(siteMap).reduce((acc, arr) => acc + (arr?.length || 0), 0);
    return { totalMasters, totalSites };
  }, [masterSites, siteMap]);

  return (
    <div className="master-site-container">
      <div className="ms-topbar">
        <div className="ms-head">
          <div className="ms-title">Master Sites</div>
          <div className="ms-subtitle">
            Manage MasterSites, view daily totals, export reports and maps.
          </div>
        </div>

        <div className="ms-stats">
          <div className="ms-stat">
            <div className="ms-stat-label">Masters</div>
            <div className="ms-stat-value">{stats.totalMasters}</div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-label">Sites</div>
            <div className="ms-stat-value">{stats.totalSites}</div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-label">Water (Selected Day)</div>
            <div className="ms-stat-value">
              {Number.isFinite(totals.water) ? totals.water.toFixed(0) : "0"} L
            </div>
          </div>
          <div className="ms-stat">
            <div className="ms-stat-label">₹ (Selected Day)</div>
            <div className="ms-stat-value">
              {Number.isFinite(totals.amount) ? totals.amount.toFixed(0) : "0"}
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <>
          <h2 className="master-site-title">
            {editingId ? "✏️ Edit MasterSite" : "➕ Add MasterSite"}
            {editingId && (
              <button
                type="button"
                className="ms-mini-btn"
                onClick={() => {
                  setEditingId(null);
                  setForm({
                    name: "",
                    dailyDataAvailableFrom: "",
                    dailyDataAvailableUpto: "now",
                    dispenserType: "",
                  });
                }}
              >
                Cancel
              </button>
            )}
          </h2>

          <form onSubmit={handleSubmit} className="master-form">
            <input
              type="text"
              placeholder="MasterSite Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />

            <DatePicker
              selected={
                form.dailyDataAvailableFrom
                  ? new Date(
                    form.dailyDataAvailableFrom.split("/").reverse().join("-")
                  )
                  : null
              }
              onChange={(date) => {
                if (!date) {
                  setForm({ ...form, dailyDataAvailableFrom: "" });
                  return;
                }

                const dd = String(date.getDate()).padStart(2, "0");
                const mm = String(date.getMonth() + 1).padStart(2, "0");
                const yyyy = date.getFullYear();

                setForm({
                  ...form,
                  dailyDataAvailableFrom: `${dd}/${mm}/${yyyy}`,
                });
              }}
              dateFormat="dd/MM/yyyy"
              placeholderText="Available From"
              className="input-control"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              isClearable
            />

            <input
              type="text"
              placeholder="Available Upto (or now)"
              value={form.dailyDataAvailableUpto}
              onChange={(e) => setForm({ ...form, dailyDataAvailableUpto: e.target.value })}
            />

            <select
              value={form.dispenserType || ""}
              onChange={(e) => setForm({ ...form, dispenserType: e.target.value })}
            >
              <option value="">Select Dispenser Type</option>
              {dispenserTypes.map((type, idx) => (
                <option key={idx} value={type.dispenserType}>
                  {type.dispenserType}
                </option>
              ))}
            </select>

            <button className="btn fetch" type="submit" disabled={loading}>
              {editingId ? "Update" : "Add"}
            </button>
          </form>
        </>
      )}

      {/* ✅ Controls row: date + search */}
      <div className="ms-controls">
        <div className="report-date-section ms-date">
          <label>Select Report Date:</label>
          <DatePicker
            selected={reportDateObj}
            onChange={(date) => {
              if (!date) {
                setReportDateObj(null);
                setReportDate("");
                return;
              }
              setReportDateObj(date);
              setReportDate(toDDMMYYYY(date));
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date"
            className="input-control"
            popperPlacement="bottom-start"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            isClearable
          />
          {loadingReports && <span className="ms-chip ms-chip-loading">Loading…</span>}
        </div>

        {/* ✅ Search with button */}
        <div className="ms-search">
          <input
            className="input-control"
            placeholder="Search master/site/phone/type…"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runSearch();
              }
            }}
          />

          <div className="ms-search-actions">
            <button
              type="button"
              className="ms-mini-btn ms-search-btn"
              onClick={runSearch}
              disabled={loading}
            >
              Search
            </button>

            <button
              type="button"
              className="ms-mini-btn"
              onClick={clearSearch}
              disabled={!draftQuery && !appliedQuery}
            >
              Clear
            </button>

            {!!appliedQuery && <span className="ms-chip ms-chip-date">Filtering: {appliedQuery}</span>}
          </div>
        </div>
      </div>

      {errMsg && <div className="ms-error">⚠️ {errMsg}</div>}

      <h2 className="master-title">Existing Master Sites</h2>

      <table className="report-table">
        <thead>
          <tr>
            <th>Master Site</th>
            <th>Sites</th>
            <th>Water Dispensed (L)</th>
            <th>Amount Collected (₹)</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td colSpan="5">
                <div className="ms-loading">Loading master sites…</div>
              </td>
            </tr>
          )}

          {!loading && filteredMasterSites.length === 0 && (
            <tr>
              <td colSpan="5">
                <div className="ms-empty">No results. Try a different search.</div>
              </td>
            </tr>
          )}

          {!loading &&
            filteredMasterSites.map((ms) => {
              const reports = reportMap[ms._id] || [];
              const sites = siteMap[ms._id] || [];

              const totalWater = reports.reduce((sum, r) => sum + parseDecimal(r.totalWaterDispensed), 0);
              const totalAmount = reports.reduce((sum, r) => sum + parseDecimal(r.totalAmountCollected), 0);

              const isExpanded = !!expanded[ms._id];
              const isMapOpen = !!mapOpen[ms._id];

              return (
                <React.Fragment key={ms._id}>
                  <tr>
                    <td>
                      <div className="ms-cell-title">
                        <div className="ms-name">{ms.name}</div>
                        <div className="ms-meta">
                          <span className="ms-chip">Type: {ms.dispenserType || "-"}</span>
                          <span className="ms-chip">Sites: {sites.length}</span>
                          {reportDate && <span className="ms-chip ms-chip-date">Date: {reportDate}</span>}
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="ms-sites-head">
                        <button
                          type="button"
                          className="ms-mini-btn"
                          onClick={() => setExpanded((p) => ({ ...p, [ms._id]: !p[ms._id] }))}
                        >
                          {isExpanded ? "Hide Sites" : "Show Sites"}
                        </button>

                        <button
                          type="button"
                          className="ms-mini-btn"
                          onClick={() => setMapOpen((p) => ({ ...p, [ms._id]: !p[ms._id] }))}
                        >
                          {isMapOpen ? "Hide Map" : "Show Map"}
                        </button>
                      </div>

                      {isExpanded && (
                        <ol className="site-list">
                          {sites.map((s) => {
                            const rep = reports.find((r) => r.siteId === s._id);
                            const water = parseDecimal(rep?.totalWaterDispensed);
                            const amt = parseDecimal(rep?.totalAmountCollected);

                            return (
                              <li key={s._id} className="site-item">
                                <div className="site-left">
                                  <div className="site-name">{s.name}</div>
                                  <div className="site-phone">{s.phoneNumber || "-"}</div>
                                </div>

                                <div className="site-metrics">
                                  <span className="metric-pill">
                                    <span className="metric-label">Water</span>
                                    <span className="metric-value">{Number.isFinite(water) ? water.toFixed(0) : "0"} L</span>
                                  </span>

                                  <span className="metric-pill metric-rupee">
                                    <span className="metric-label">₹</span>
                                    <span className="metric-value">{Number.isFinite(amt) ? amt.toFixed(0) : "0"}</span>
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      )}
                    </td>

                    <td>{Number.isFinite(totalWater) ? totalWater.toFixed(2) : "0.00"}</td>
                    <td>{Number.isFinite(totalAmount) ? totalAmount.toFixed(2) : "0.00"}</td>

                    <td>
                      <div className="ms-actions">
                        {isAdmin && (
                          <>
                            <button type="button" className="edit-button" onClick={() => handleEdit(ms)}>
                              Edit
                            </button>
                            <button type="button" className="delete-button" onClick={() => handleDelete(ms._id)}>
                              Delete
                            </button>
                          </>
                        )}

                        <button
                          type="button"
                          className="export-button"
                          onClick={() => exportToExcel(ms._id)}
                          disabled={!reportDate || !(reportMap[ms._id]?.length)}
                        >
                          Export
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isMapOpen && (
                    <tr>
                      <td colSpan="5">
                        <div className="ms-map-block">
                          <div className="ms-map-title">📍 Location Map</div>
                          <MapViewer sites={sites.filter((s) => s.latitude && s.longitude)} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}